import React, { useContext, useEffect, useState } from 'react';
import { List, ListHeader, DataColumn, LinkColumn } from 'components/lists';
import { useFind } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { DropDownList } from 'components/formInputs/dropDownList';
import { PageContent } from 'components/layouts/pageContent';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { Pagination } from 'components/lists/pagination';
import { formFileUrlFromFileArray } from 'utils/imageHelper';

export const RestaurantPromotions = () => {
  const { state: auth } = useContext(AuthContext);
  const {
    state: companyState,
    getCompanyRestaurants,
    deleteById,
    shouldDisplayNoRestaurantBlock,
  } = useContext(CompanyContext);

  const [currentPage, setCurrentPage] = useState(1);

  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(
    `promotions-filter-restaurant-${auth.company?.company}`,
    null,
  );

  const navigate = useNavigate();

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANT_PROMOTIONS,
    secretKey: auth.token,
    filterBuilder: () => {
      const filter = { company: { $oid: auth.company?.company } };

      if (filterRestaurantRef.value) {
        filter.restaurants = { $oid: filterRestaurantRef.value };
      }

      return filter;
    },
    projection: {
      _id: 1,
      name: 1,
      image_cover: 1,
      hidden: 1,
      order: 1,
    },
    excludeCulture: true,
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentPage - 1,
    loadOnInit: true,
    resourceV2: 'AdminRestaurantPromotion',
    companyIdForV2: auth.company?.company,
  };

  const [findPromotions, restaurantPromotions, isLoading, totalCount] = useFind(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onFilterPromotions = () => {
    findPromotions();
  };

  const onClearFilters = () => {
    setFilterRestaurant('');
    onFilterPromotions();
  };

  const onEditClicked = (item) => {
    navigate(`/additional/promotions/${item._id}`);
  };

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.RESTAURANT_PROMOTIONS,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminRestaurantPromotion',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        await findPromotions(requestData, true);
      },
    );
  };

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  return (
    <ListLayout
      isLoading={
        isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants
      }
    >
      <PageTitle
        title="Promotions"
        isTopLevel
      />
      <PageContent>
        <FilterBlock
          buttons={[
            {
              onPress: onClearFilters,
              text: 'Clear',
              class:
                'bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
              disabled: !!isLoading,
            },
            {
              onPress: onFilterPromotions,
              text: 'Search',
              class:
                'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
              disabled: !!isLoading,
              isLoading: !!isLoading,
            },
          ]}
        >
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              value={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
              }}
              options={[
                { label: 'All', value: '' },
                ...(companyState.restaurants || []).map((restaurant) => ({
                  label: restaurant.name,
                  value: restaurant._id,
                })),
              ]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        <ListHeader title="Promotions" description={`Promotions of ${auth.company?.display_name}`}>
          <button
            type="button"
            onClick={() => navigate(`/additional/promotions/add-new?restaurant=${filterRestaurant}`)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>
        <List source={restaurantPromotions} onRowClick={onEditClicked}>
          <DataColumn
            name="Image"
            render={(employee) => {
              const imageUrl = formFileUrlFromFileArray(employee.image_cover);

              if (!imageUrl) {
                return null;
              }

              return (
                <div
                  className="w-10 h-10 rounded-lg bg-no-repeat bg-center bg-cover"
                  style={{ backgroundImage: `url(${imageUrl})` }}
                />
              );
            }}
            dbColumnName="image"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-700 sm:table-cell"
          />
          <DataColumn
            name="Name"
            dbColumnName="name"
            headerClass="px-3 py-3.5 text-left text-sm
              font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Order"
            dbColumnName="order"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <DataColumn
            name="Hidden"
            dbColumnName="hidden"
            headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
            dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
          />
          <LinkColumn
            title="Delete"
            onClick={onDeleteClicked}
            askForConfirmation
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
          />
          <Pagination
            pageSize={Config.DEFAULTS.PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChanged={async (pageNumber) => {
              const request = { ...requestData, pageNumber: pageNumber - 1 };

              await findPromotions(request, false);
              setCurrentPage(pageNumber);
            }}
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
